import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { RoomEvent } from "livekit-client";
import { Fullscreen } from "components/shared/Fullscreen/Fullscreen";
import { Badge, ListGroupItemHeading, Button, H5 } from "@bootstrap-styled/v4";
import { useActivity } from "hooks/useActivity/useActivity";
import { texts } from "config/texts";
import { useMembers } from "hooks/livekit/useMembers/useMembers";
import { useUser } from "hooks/useUser/useUser";
import { useSessionActivity } from "hooks/useSessionActivity/useSessionActivity";
import { useSessionContext } from "hooks/useSession";
import { useAuthUserDataContext } from "hooks/useAuthUserData/useAuthUserData";
import { useReadyRoomHeartbeat } from "hooks/useReadyRoomHeartbeat/useReadyRoomHeartbeat";
import { useDataTrack } from "hooks/livekit/useDataTrack/useDataTrack";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import { request } from "hooks/useOkoApi/useOkoApi";
import {
  createTimeoutSignal,
  redirectToGroupCode,
  REDIRECT_ERROR,
  SESSION_STATUS,
} from "components/pages/Session/sessionPrefetch";
import { auth } from "hooks/useFirebase";
import { VideosBar } from "components/pages/Session/shared/VideosBar/VideosBar";
import { ScreenTitle } from "components/shared/ScreenTitle/ScreenTitle";
import { colors, constants, logSession } from "utils";
import PaperPlane from "assets/PaperPlane.gif";

// A healthy session leaves this screen in a few seconds, but the orchestrator
// may legitimately wait up to ~10s post-init for waiting-room speech to drain —
// so the warn threshold sits right at that edge and is env-tunable for product.
const INIT_WARN_MS = Number(process.env.REACT_APP_INIT_WARN_MS) || 10000;
const INIT_FAIL_MS = Number(process.env.REACT_APP_INIT_FAIL_MS) || 20000;

export const WaitingRoom = () => {
  const {
    session,
    groupCode,
    userRole,
    authMethod,
    setWaitingRoomInit,
    waitingRoomEnteredAtRef,
  } = useSessionContext();
  const { authUserData } = useAuthUserDataContext();
  const { change: changeActivity } = useSessionActivity();
  const { update: updateActivity } = useActivity();
  const { activity, loading } = useActivity();
  const { members } = useMembers({ includeObservers: false });
  const { room } = useVideoContext();
  const [hasVoted, setHasVoted] = useState(false);
  const [showSlowWarning, setShowSlowWarning] = useState(false);
  const advancedRef = useRef(false);
  // A stamp already present at first render means this is a remount after a
  // connection blip (the heartbeat hook only stamps on genuine entry). One
  // resync is owed for it, sent as soon as the session id is known.
  const remountResyncPendingRef = useRef(waitingRoomEnteredAtRef?.current != null);

  // Funnel success terminal: the session has actually initialized and is
  // advancing into the first activity. This is the one event that proves a
  // user made it through the waiting funnel — its absence (with preparing
  // heartbeats still firing) is the signal that a user is stuck. `via` says
  // which signal got us here (the live state_change or a resync pull).
  const advance = (via) => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    logSession("Session activity started", {
      sessionId: session?.id,
      groupCode,
      userId: authUserData?.id,
      role: userRole,
      authMethod,
      durationMs: getElapsedMs(),
      activityId: constants.ACTIVITY_ID_MULTIPLE_CHOICE,
      fromRoom: "Waiting room (OKO preparing)",
      via,
    });
    reportExit("advanced");
    changeActivity({ id: constants.ACTIVITY_ID_MULTIPLE_CHOICE });
  };

  // Level-triggered backstop for the edge-triggered state_change below. A
  // connection blip swallows that message for good (no replay), leaving the
  // student on this screen while the session runs on. So ask the server which
  // phase the session is in after a blip and before giving up. Only after a
  // blip: the server flips to in_progress several seconds before it sends the
  // first screen, so a check on a fresh entry would skip this screen early on
  // every normal join. Resolves true when the session had started and we advanced.
  const resync = async (via) => {
    if (!session?.id) return false;
    try {
      const res = await request(`session/?session_id=${session.id}`, {
        method: "GET",
        signal: createTimeoutSignal(5000),
      });
      if (res?.status === SESSION_STATUS.IN_PROGRESS) {
        advance(via);
        return true;
      }
      if (res?.waiting_room) setWaitingRoomInit(res.waiting_room);
    } catch (e) {
      logSession("Waiting room resync failed", {
        sessionId: session?.id,
        groupCode,
        userId: authUserData?.id,
        via,
        errorName: e?.name,
        statusCode: e?.status,
      });
    }
    return false;
  };

  // A full disconnect swaps the Join screen in and remounts this component.
  useEffect(() => {
    if (!remountResyncPendingRef.current || !session?.id) return;
    remountResyncPendingRef.current = false;
    resync("resync_remount");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // LiveKit's in-place resume: the room object survives, so no remount. This is
  // the path a short wifi blip takes.
  useEffect(() => {
    if (typeof room?.on !== "function") return;
    const onReconnected = () => resync("resync_reconnected");
    room.on(RoomEvent.Reconnected, onReconnected);
    return () => room.off(RoomEvent.Reconnected, onReconnected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, session?.id]);

  // Self-healing: if session state_change events arrive while WaitingRoom is
  // still showing, it means the session has already progressed past the
  // waiting phase. Correct Firestore to move to multiple-choice.
  useDataTrack((e) => {
    if (e?.event_type === "state_change") {
      advance("state_change");
      return;
    }
    // The orchestrator's watchdog gave up on initializing this session; it has
    // already ended it server-side, so redirect without a report call.
    if (e?.event_type === "waiting_room_timeout") {
      failSession({ source: "waiting_room_timeout", reportToServer: false });
    }
  });

  // Heartbeat logging for waiting room (OKO is preparing) tracking.
  // A healthy session initializes in a few seconds; staying here past the warn
  // threshold means init is struggling, and past the fail threshold means it
  // failed — tell the server (which ends the session) and send the student
  // back to the group-code screen for a fresh start.
  const { reportExit, getElapsedMs } = useReadyRoomHeartbeat({
    roomName: "Waiting room (OKO preparing)",
    sessionId: session?.id,
    groupCode,
    userId: authUserData?.id,
    hasClickedBegin: hasVoted,
    participantCount: members?.length || 0,
    role: userRole,
    authMethod,
    warnThresholdMs: INIT_WARN_MS,
    failThresholdMs: INIT_FAIL_MS,
    enteredAtRef: waitingRoomEnteredAtRef,
    onWarn: () => {
      setShowSlowWarning(true);
      logSession("Waiting room init slow warning shown", {
        sessionId: session?.id,
        groupCode,
        userId: authUserData?.id,
        role: userRole,
        authMethod,
        durationMs: getElapsedMs(),
      });
      request(`session/${session?.id}/report_init_failure`, {
        method: "POST",
        payload: { phase: "warned" },
      }).catch(() => {});
    },
    // Last look before ejecting the student: after a long blip the clock may
    // already be past the threshold on remount, and the session may have
    // started meanwhile.
    onFail: async () => {
      if (await resync("resync_fail_threshold")) return;
      failSession({ source: "init_failure_timeout", reportToServer: true });
    },
  });

  const failSession = async ({ source, reportToServer }) => {
    reportExit("init_failed");
    if (reportToServer) {
      // Best-effort with a short timeout: the server's own watchdog covers
      // cleanup if this call never lands, and the redirect must not hang on it.
      try {
        await request(`session/${session?.id}/report_init_failure`, {
          method: "POST",
          payload: { phase: "failed" },
          signal: createTimeoutSignal(5000),
        });
      } catch (_e) {}
    }
    redirectToGroupCode(groupCode, REDIRECT_ERROR.SESSION_INIT_FAILED, {
      source,
      sessionId: session?.id,
    });
  };

  const onStart = async () => {
    const res = await request(`session/${session?.id}/vote_to_start`);
    setHasVoted(true);

    if (res?.votes_so_far === res?.total_votes_required) {
      changeActivity({ id: "waiting-room" });
      updateActivity(
        {
          title: texts.waitingRoomDefaultTitle,
          showParticipants: false,
          showParticipantVideos: false,
        },
        "waiting-room"
      );
    }
  };

  return (
    <StyledFullscreen>
      <>
        {!loading && <ScreenTitle name="Waiting room screen" />}
        {!loading && (
          <div style={{ display: "flex", flexDirection: "column", fontSize: "36px", fontWeight: "500", gap: "64px" }}>
              <span role="status" aria-live="polite" aria-atomic="true">
                {activity?.title === undefined
                  ? texts.waitingRoomDefaultTitle
                  : activity?.title}
                {showSlowWarning && (
                  <SlowWarning>{texts.waitingRoomSlowWarning}</SlowWarning>
                )}
              </span>
            {!activity?.hideLoadingIndicator && (
              <div style={{ alignSelf: "center" }}>
                <img src={PaperPlane} alt="Loading" />
              </div>
            )}
            {activity?.showExitButton && (
              <Button
                onClick={async () => {
                  await auth.signOut();
                  window.location.href = "https://okolabs.ai/";
                }}
                className="mt-3"
                size="lg"
              >
                OK
              </Button>
            )}
            {activity?.showParticipants && (
              <div>
                <ListGroupItemHeading role="status" aria-live="polite" aria-atomic="true">
                  {members.length <= 1 && "Waiting for others to join..."}
                  {members.length > 1 &&
                    "Is this everyone in your OKO group?"}
                </ListGroupItemHeading>
                <H5>
                  {members.map((m) => (
                    <Badge className="mr-2" key={m.participant.id}>
                      <Name participant={m.participant} />
                    </Badge>
                  ))}
                </H5>
                {members.length > 1 && !hasVoted && (
                  <Button onClick={onStart} className="mt-3">
                    Yes that's everyone - let's start
                  </Button>
                )}
                {/* <GroupCodeLabel>Group Code: {groupCode}</GroupCodeLabel> */}
              </div>
            )}
          </div>
        )}

        <div
          style={{
            width: "96vw",
            marginTop: 10,
            opacity: activity?.showParticipantVideos ? 1 : 0,
          }}
        >
          <VideosBar />
        </div>
      </>
    </StyledFullscreen>
  );
};

const StyledFullscreen = styled(Fullscreen)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${colors.milkGlass500};
`;

const SlowWarning = styled.span`
  display: block;
  margin-top: 16px;
  font-size: 20px;
  font-weight: 400;
  max-width: 640px;
`;

const GroupCodeLabel = styled.p`
  font-size: 14px;
  color: ${colors.milkGlass950};
  margin: 10px 0 0 0;
`;

const Name = ({ participant }) => {
  const { user } = useUser(participant.userId);
  return <span>{user?.displayName}</span>;
};
